import {
  Button,
  Modal,
  Divider,
  Form,
  Spin,
  message,
  Row,
  Image,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { getAppCreatedAtMillis, getCachedApps } from "../../utils/appCache";
import {
  getProductGroupByApp,
  getProductGroupOptionProducts,
  invalidateProductGroupCache,
  resetProductGroupRequests,
} from "../../services/adminProductGroupCache";
import { warmupAdminFunction } from "../../utils/adminFunctionWarmup";
import { logAdminDiagnostic } from "../../utils/clientDiagnostics";

const normalizeAppKey = (value) =>
  String(value || "").trim().toLowerCase();

const TOUCH_CLICK_DEDUPE_MS = 500;

const StableInlineSelect = ({
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  diagnosticField = "unknown",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeWhenOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeWhenOutside);
    return () => document.removeEventListener("pointerdown", closeWhenOutside);
  }, [menuOpen]);

  return (
    <div
      ref={rootRef}
      className={`neo-stable-select ${menuOpen ? "open" : ""}`}
    >
      <button
        type="button"
        className="neo-stable-select-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => {
          logAdminDiagnostic("create-order-select", {
            field: diagnosticField,
            action: menuOpen ? "close" : "open",
            optionCount: options.length,
            disabled,
          });
          setMenuOpen((current) => !current);
        }}
      >
        <span className={selectedOption ? "" : "placeholder"}>
          {selectedOption?.label || placeholder}
        </span>
        <span className="neo-stable-select-arrow">⌄</span>
      </button>

      {menuOpen && (
        <div className="neo-stable-select-menu" role="listbox">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              disabled={option.disabled}
              className={`neo-stable-select-option ${
                option.value === value ? "selected" : ""
              }`}
              onClick={() => {
                if (option.disabled) return;
                logAdminDiagnostic("create-order-select", {
                  field: diagnosticField,
                  action: "choose",
                  optionCount: options.length,
                });
                onChange?.(option.value);
                setMenuOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// เริ่ม clipboard write ตั้งแต่จังหวะกดปุ่ม เพื่อรักษา user gesture บนมือถือ
// แล้วค่อยเติมข้อความจริงเมื่อ API สร้าง order เสร็จ
const startDeferredClipboardCopy = () => {
  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    return null;
  }

  let resolveText;
  const textPromise = new Promise((resolve) => {
    resolveText = resolve;
  });

  try {
    const writePromise = navigator.clipboard.write([
      new ClipboardItem({ "text/plain": textPromise }),
    ]);

    // ป้องกัน unhandled rejection ระหว่างรอ API ตอบกลับ
    writePromise.catch(() => {});

    return {
      resolve: (text) =>
        resolveText(new Blob([text || ""], { type: "text/plain" })),
      promise: writePromise,
    };
  } catch (error) {
    console.warn("Deferred clipboard is not available:", error);
    return null;
  }
};

export default function CreateOrderModal({ fetchData }) {
  const navigate = useNavigate();
  const { Text } = Typography;
  const [open, setOpen] = useState(false);
  const [findLoading, setFindLoading] = useState(false);
  const [optionsList, setOptionsList] = useState();
  const [productList, setProductList] = useState([]);
  const [screenList, setScreenList] = useState([]);
  const [selectApp, setSelectApp] = useState();
  const [selectedAppId, setSelectedAppId] = useState();
  const [selectOption, setSelectOption] = useState();
  const [selectProduct, setSelectProduct] = useState();
  const [appData, setAppData] = useState();
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [modalRecoveryVersion, setModalRecoveryVersion] = useState(0);
  const productGroupRequestRef = useRef(0);
  const optionProductsRequestRef = useRef(0);
  const dynamicFieldsRef = useRef(null);
  const lastTouchAppSelectionRef = useRef({
    appIdentity: null,
    handledAt: 0,
  });

  const [form] = Form.useForm();

  useEffect(() => {
    const lockClass = "create-order-modal-scroll-lock";

    document.documentElement.classList.toggle(lockClass, open);
    document.body.classList.toggle(lockClass, open);

    return () => {
      document.documentElement.classList.remove(lockClass);
      document.body.classList.remove(lockClass);
    };
  }, [open]);

  useEffect(() => {
    const handleResume = () => {
      logAdminDiagnostic("create-order-resume", {
        modalOpen: open,
        loadingCreate,
        selectedApp: Boolean(selectApp),
        selectedOption: Boolean(selectOption),
      });
      // iOS Safari can leave a suspended request and the modal's composited
      // touch layer alive after the tab returns to the foreground. Ignore any
      // late result, release the shared pending-request entries, and rebuild
      // the modal DOM so the first tap after resume is handled normally.
      productGroupRequestRef.current += 1;
      optionProductsRequestRef.current += 1;
      resetProductGroupRequests();
      invalidateProductGroupCache();
      setModalRecoveryVersion((current) => current + 1);
      setFindLoading(false);
      setLoadingProducts(false);

      // A create-order POST may have completed while Safari was suspended.
      // Keep that state intact to avoid enabling a duplicate submission.
      if (!open || loadingCreate) return;

      setSelectApp(undefined);
      setSelectedAppId(undefined);
      setSelectOption(undefined);
      setSelectProduct(undefined);
      setOptionsList(undefined);
      setProductList([]);
      setScreenList([]);
      form.resetFields(["optionId", "product", "screen"]);
    };

    window.addEventListener("cinema:resume", handleResume);
    return () => window.removeEventListener("cinema:resume", handleResume);
  }, [form, loadingCreate, open, selectApp, selectOption]);

  const visibleApps = useMemo(() => {
    const seenApps = new Set();

    return (appData || []).filter((app) => {
      const appKey = normalizeAppKey(app?.nameLower || app?.name);
      if (!appKey || seenApps.has(appKey)) return false;
      seenApps.add(appKey);
      return true;
    });
  }, [appData]);

  const selectedOptionData = optionsList?.options?.find(
    (item) => item.optionId === selectOption,
  );
  const isPermanentApp = optionsList?.appType === "permanent";
  const isOutOfStock =
    !isPermanentApp && Number(selectedOptionData?.stock || 0) <= 0;

  useEffect(() => {
    if (!open || !selectedAppId) return undefined;

    // Wait until React and Safari have both committed the visual state. The
    // request-success event alone cannot prove that the customer actually saw
    // the active app or its options.
    let secondFrameId;
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        const activeButton = Array.from(
          document.querySelectorAll("[data-create-order-app]"),
        ).find(
          (button) => button.dataset.createOrderApp === selectedAppId,
        );
        const optionCount = Array.isArray(optionsList?.options)
          ? optionsList.options.length
          : 0;

        logAdminDiagnostic("create-order-app-request", {
          phase: "render-confirmed",
          app: selectApp || "",
          renderState: findLoading
            ? "loading"
            : optionsList?.groupId
              ? "ready"
              : "empty",
          activeButton: activeButton?.classList.contains("active") === true,
          buttonDisabled: activeButton?.disabled === true,
          optionCount,
          dynamicFieldsVisible: Boolean(dynamicFieldsRef.current),
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrameId);
      if (secondFrameId) window.cancelAnimationFrame(secondFrameId);
    };
  }, [findLoading, open, optionsList, selectApp, selectedAppId]);

  const fetchAppData = async () => {
    try {
      const list = (await getCachedApps())
        .filter((app) => app?.status === "available")
        .sort(
          (left, right) =>
            getAppCreatedAtMillis(right) - getAppCreatedAtMillis(left),
        );
      setAppData(list);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const showModal = async () => {
    logAdminDiagnostic("create-order-open", {
      cachedAppCount: Array.isArray(appData) ? appData.length : 0,
    });
    warmupAdminFunction("createManualOrder");
    productGroupRequestRef.current += 1;
    optionProductsRequestRef.current += 1;
    resetProductGroupRequests();
    invalidateProductGroupCache();
    setSelectApp(undefined);
    setSelectedAppId(undefined);
    setSelectOption(undefined);
    setSelectProduct(undefined);
    setOptionsList(undefined);
    setProductList([]);
    setScreenList([]);
    form.resetFields();
    setOpen(true);
    await fetchAppData();
  };

  const createOrder = async (value) => {
    logAdminDiagnostic("create-order-submit", {
      phase: "start",
      app: selectApp || "",
      permanent: isPermanentApp,
    });
    setLoadingCreate(true);
    const deferredClipboard = startDeferredClipboardCopy();

    try {
      const { data } = await axios.post(
        "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/createManualOrder",
        {
          app: selectApp,
          groupId: optionsList.groupId,
          optionId: value.optionId,
          productId: value?.product || null,
          screen: value?.screen || null,
        },
      );

      form?.resetFields();
      let copied = true;

      if (deferredClipboard) {
        deferredClipboard.resolve(data.claimMessage);

        try {
          await deferredClipboard.promise;
        } catch (clipboardError) {
          copied = false;
          console.warn("Deferred copy claim message failed:", clipboardError);
        }
      } else {
        try {
          await navigator.clipboard.writeText(data.claimMessage || "");
        } catch (clipboardError) {
          // การสร้าง order สำเร็จแล้ว ห้ามให้ clipboard error ทำให้หน้าเข้า catch
          copied = false;
          console.warn("Copy claim message failed:", clipboardError);
        }
      }

      navigate("/order", {
        state: {
          orderId: data.orderId,
          statusTab: "success",
        },
      });

      message.success(
        copied
          ? "สร้างรายการและคัดลอกข้อความแล้ว"
          : "สร้างรายการสำเร็จ แต่ไม่สามารถคัดลอกข้อความอัตโนมัติได้",
      );
      invalidateProductGroupCache(selectApp);
      fetchData();
      setOpen(false);
      logAdminDiagnostic("create-order-submit", {
        phase: "success",
        app: selectApp || "",
        permanent: isPermanentApp,
      });
    } catch (error) {
      // จบ pending clipboard หาก API สร้าง order ไม่สำเร็จ
      deferredClipboard?.resolve("");
      console.error("Clone Product Error", error);
      logAdminDiagnostic("create-order-submit", {
        phase: "error",
        app: selectApp || "",
        statusCode: Number(error?.response?.status || 0),
      });
      message.error(
        error.response?.data?.message ||
          "ไม่สามารถสร้างคำสั่งซื้อได้ กรุณาลองใหม่อีกครั้ง",
      );
    } finally {
      setLoadingCreate(false);
    }
  };

  const selectAppCheck = async (app) => {
    const name = app?.nameLower;
    const appIdentity = app?.key || normalizeAppKey(name || app?.name);
    if (!name || !appIdentity) {
      message.error("ข้อมูล App ไม่ถูกต้อง");
      return;
    }

    const requestId = ++productGroupRequestRef.current;
    logAdminDiagnostic("create-order-app-request", {
      phase: "start",
      requestId,
      app: name,
    });
    optionProductsRequestRef.current += 1;
    if (dynamicFieldsRef.current) {
      dynamicFieldsRef.current.scrollTop = 0;
    }
    setSelectApp(name);
    setSelectedAppId(appIdentity);
    setSelectOption(undefined);
    setSelectProduct(undefined);
    setProductList([]);
    setScreenList([]);
    setOptionsList(undefined);
    form.resetFields();
    setFindLoading(true);
    try {
      const data = await getProductGroupByApp(name);
      if (requestId !== productGroupRequestRef.current) {
        logAdminDiagnostic("create-order-app-request", {
          phase: "stale",
          requestId,
          app: name,
        });
        return;
      }
      if (data && normalizeAppKey(data?.app) !== normalizeAppKey(name)) {
        throw new Error("ตัวเลือกสินค้าไม่ตรงกับ App ที่เลือก กรุณาลองใหม่");
      }
      setOptionsList(data);
      logAdminDiagnostic("create-order-app-request", {
        phase: "success",
        requestId,
        app: name,
        foundGroup: Boolean(data?.groupId),
        optionCount: Array.isArray(data?.options) ? data.options.length : 0,
      });
    } catch (error) {
      if (requestId !== productGroupRequestRef.current) return;
      console.error("Clone Product Error", error);
      logAdminDiagnostic("create-order-app-request", {
        phase: "error",
        requestId,
        app: name,
        statusCode: Number(error?.response?.status || 0),
      });
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "ไม่สามารถโหลดตัวเลือกสินค้าได้",
      );
    } finally {
      if (requestId === productGroupRequestRef.current) {
        setFindLoading(false);
      }
    }
  };

  const selectOptionCheck = async (optionId) => {
    const requestId = ++optionProductsRequestRef.current;
    logAdminDiagnostic("create-order-option-request", {
      phase: "start",
      requestId,
      app: selectApp || "",
    });
    setSelectOption(optionId);
    setSelectProduct(undefined);
    setProductList([]);
    setScreenList([]);
    form.setFieldValue("product", undefined);
    form.setFieldValue("screen", undefined);

    const option = optionsList?.options?.find(
      (item) => item.optionId === optionId,
    );

    if (!optionId || isPermanentApp || Number(option?.stock || 0) <= 0) {
      setLoadingProducts(false);
      if (optionId && !isPermanentApp && Number(option?.stock || 0) <= 0) {
        message.error("สินค้าหมด กรุณาเติมสินค้าก่อนสร้างคำสั่งซื้อ");
      }
      return;
    }

    setLoadingProducts(true);
    try {
      const products = await getProductGroupOptionProducts(
        selectApp,
        optionsList?.groupId,
        optionId,
      );
      if (requestId !== optionProductsRequestRef.current) {
        logAdminDiagnostic("create-order-option-request", {
          phase: "stale",
          requestId,
          app: selectApp || "",
        });
        return;
      }
      setProductList(products);
      logAdminDiagnostic("create-order-option-request", {
        phase: "success",
        requestId,
        app: selectApp || "",
        productCount: Array.isArray(products) ? products.length : 0,
      });
    } catch (error) {
      if (requestId !== optionProductsRequestRef.current) return;
      console.error("Load option products error:", error);
      logAdminDiagnostic("create-order-option-request", {
        phase: "error",
        requestId,
        app: selectApp || "",
        statusCode: Number(error?.response?.status || 0),
      });
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "ไม่สามารถโหลดสินค้าได้",
      );
    } finally {
      if (requestId === optionProductsRequestRef.current) {
        setLoadingProducts(false);
      }
    }
  };

  const closeModal = () => {
    productGroupRequestRef.current += 1;
    optionProductsRequestRef.current += 1;
    setOpen(false);
    form?.resetFields();
    setSelectApp(undefined);
    setSelectedAppId(undefined);
    setSelectOption(undefined);
    setSelectProduct(undefined);
    setOptionsList(undefined);
    setProductList([]);
    setScreenList([]);
    setFindLoading(false);
    setLoadingProducts(false);
  };

  return (
    <>
      <Button
        onClick={showModal}
        className="neo-btn-create"
        icon={<PlusOutlined />}
      >
        สร้างคำสั่งซื้อ
      </Button>

      <Modal
        key={`create-order-modal-${modalRecoveryVersion}`}
        width={500}
        open={open}
        footer={null}
        closeIcon={false}
        className="neo-modal"
        maskClosable={!loadingCreate}
        keyboard={!loadingCreate}
        onCancel={closeModal}
      >
        <div className="neo-modal-header neo-modal-header-close-left">
          <div>
            <div className="neo-modal-topline">MANAGEMENT</div>

            <div className="neo-modal-title">สร้างคำสั่งซื้อ</div>
          </div>

          <Button
            className="neo-close-btn"
            disabled={loadingCreate}
            onClick={() => {
              closeModal();
            }}
          >
            ✕
          </Button>
        </div>

        <Divider className="neo-divider" />

        <Form layout="vertical" form={form} onFinish={createOrder}>
          <div className="neo-create-order-app-grid">
            {visibleApps.map((item) => {
              const appIdentity =
                item?.key || normalizeAppKey(item?.nameLower || item?.name);
              const isSelected = selectedAppId === appIdentity;

              return (
                <div
                  key={appIdentity}
                  className="neo-create-order-app-col"
                >
                  <button
                    type="button"
                    data-create-order-app={appIdentity}
                    className={`neo-create-order-app-button ${
                      isSelected ? "active" : ""
                    } ${isSelected && findLoading ? "loading" : ""}`}
                    aria-pressed={isSelected}
                    aria-busy={isSelected && findLoading}
                    disabled={loadingCreate || findLoading}
                    onPointerDown={(event) => {
                      if (
                        event.pointerType !== "touch" &&
                        event.pointerType !== "pen"
                      ) {
                        return;
                      }

                      // iOS Safari can cancel a click when the finger moves a
                      // few pixels. Select immediately, but remember which
                      // app handled the touch so only that app's synthetic
                      // click is ignored. A rapid tap on another app must not
                      // be swallowed by the previous app's dedupe window.
                      event.stopPropagation();
                      logAdminDiagnostic("create-order-app-input", {
                        input: "pointerdown",
                        pointerType: event.pointerType || "unknown",
                        app: item?.nameLower || "",
                      });
                      lastTouchAppSelectionRef.current = {
                        appIdentity,
                        handledAt: Date.now(),
                      };
                      selectAppCheck(item);
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      logAdminDiagnostic("create-order-app-input", {
                        input: "click",
                        app: item?.nameLower || "",
                      });

                      const lastTouchSelection =
                        lastTouchAppSelectionRef.current;
                      if (
                        lastTouchSelection.appIdentity === appIdentity &&
                        Date.now() - lastTouchSelection.handledAt <
                          TOUCH_CLICK_DEDUPE_MS
                      ) {
                        return;
                      }

                      selectAppCheck(item);
                    }}
                  >
                    <Image
                      preview={false}
                      src={item?.logo?.url || "/noImage.jpg"}
                      className="neo-create-order-app-logo"
                    />
                    <Text className="neo-create-order-app-name">
                      {item?.name}
                    </Text>
                    {isSelected && findLoading ? (
                      <span
                        className="neo-create-order-app-loading"
                        aria-hidden="true"
                      >
                        <Spin size="small" />
                      </span>
                    ) : null}
                  </button>
                </div>
              );
            })}
          </div>
          <div
            ref={dynamicFieldsRef}
            className="neo-create-order-dynamic-fields"
          >
            {findLoading ? (
              <Row justify={"center"} style={{ padding: "50px 0px" }}>
                <Spin spinning />
              </Row>
            ) : !_.isEmpty(optionsList) ? (
              <>
              <Form.Item
                label={<span className="neo-form-label">ตัวเลือก</span>}
                name="optionId"
                rules={[
                  {
                    required: true,
                    message: "กรุณาเลือกตัวเลือก",
                  },
                ]}
              >
                <StableInlineSelect
                  diagnosticField="option"
                  placeholder="เลือกตัวเลือก"
                  onChange={selectOptionCheck}
                  options={optionsList?.options?.map((item) => {
                    if (isPermanentApp) {
                      return {
                        label: `${item?.name} (ถาวร)`,
                        value: item?.optionId,
                      };
                    }

                    const stockUnit = isPermanentApp ? "ชิ้น" : "จอ";
                    return {
                      label: `${item?.name} (${Number(item?.stock || 0)} ${stockUnit})${Number(item?.stock || 0) <= 0 ? " — สินค้าหมด" : ""}`,
                      value: item?.optionId,
                    };
                  })}
                />
              </Form.Item>

              {selectOption && isOutOfStock && (
                <div className="neo-stock-empty">
                  สินค้าหมด กรุณาเติมสินค้าก่อนสร้างคำสั่งซื้อ
                </div>
              )}

              {loadingProducts && (
                <Row justify="center" style={{ padding: "12px 0" }}>
                  <Spin spinning size="small" />
                </Row>
              )}

              {!loadingProducts &&
                !isPermanentApp &&
                selectOption &&
                productList?.length > 0 && (
                <Form.Item
                  label={<span className="neo-form-label">สินค้า</span>}
                  name="product"
                >
                  <StableInlineSelect
                    diagnosticField="product-email"
                    placeholder="เลือกสินค้า"
                    onChange={(e) => {
                      setSelectProduct(e);

                      const products = productList?.find(
                        (item) => item.productId === e,
                      );
                      setScreenList(products?.screenStatus);

                      form.setFieldValue("screen", undefined);
                    }}
                    options={productList?.map((item) => ({
                      label: item?.email,
                      value: item.productId,
                    }))}
                  />
                </Form.Item>
                )}

              {!isPermanentApp && selectProduct && screenList?.length > 0 && (
                <Form.Item
                  label={<span className="neo-form-label">โปรไฟล์</span>}
                  name="screen"
                >
                  <StableInlineSelect
                    diagnosticField="screen"
                    placeholder="เลือกโปรไฟล์"
                    options={screenList.map((item, index) => ({
                      label: `โปรไฟล์ ${item.screen}`,
                      value: item.screen,
                      disabled: item.status !== "available",
                    }))}
                  />
                </Form.Item>
              )}
              </>
            ) : (
              <Row style={{ color: "white", padding: "24px 0px" }}>
                ไม่พบตัวเลือกสินค้า
              </Row>
            )}
          </div>
          <Button
            htmlType="submit"
            disabled={!selectOption || isOutOfStock || loadingProducts}
            className="neo-primary-btn"
            block
            loading={loadingCreate}
          >
            สร้างคำสั่งซื้อ
          </Button>
        </Form>
      </Modal>
      <style>{`
      html.create-order-modal-scroll-lock,
      body.create-order-modal-scroll-lock{
        overflow-y:hidden !important;
        overscroll-behavior-y:none !important;
      }

      .neo-modal .ant-modal-content{
  padding:28px !important;
  max-height:calc(100dvh - 28px);
  overflow-y:auto;

  border-radius:28px !important;

  background:
    linear-gradient(
      180deg,
      rgba(35,26,26,.98),
      rgba(23,18,18,.98)
    ) !important;

  border:1px solid rgba(255,255,255,.08);

  box-shadow:
    0 25px 80px rgba(0,0,0,.55);
}

/* HEADER */

.neo-modal-header{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
}

.neo-modal-topline{
  color: #fc950f !important;

  font-size:12px;
  font-weight:900;

  letter-spacing:3px;
}

.neo-modal-title{
  margin-top:8px;

  color:white;

  font-size:40px;
  font-weight:900;
}

.neo-close-btn{
  width:40px;
  height:40px;

  border:none !important;

  border-radius:14px;

  background:rgba(255,255,255,.08) !important;

  color:white !important;
}

.neo-close-btn:hover{
  background:rgba(229,49,42,.18) !important;
}

/* LABEL */

.neo-form-label{
  color:#fbbf24;

  font-size:14px;
  font-weight:700;

  margin-bottom:8px;
}

/* =========================
   SELECT
========================= */

.neo-input-modal.ant-select{
  width:100%;
  background:transparent !important;
}

.neo-input-modal.ant-select .ant-select-selector{
  height:52px !important;

  display:flex !important;
  align-items:center !important;

  background:
    rgba(255,255,255,.06) !important;

  border:
    1px solid rgba(255,255,255,.08) !important;

  border-radius:16px !important;

  color:white !important;

  box-shadow:none !important;
}

.neo-input-modal.ant-select-focused .ant-select-selector{
  border-color:#fbbf24 !important;

  box-shadow:
    0 0 0 3px rgba(251,191,36,.12) !important;
}

.neo-input-modal .ant-select-selection-item{
  color:white !important;
}

.neo-input-modal .ant-select-selection-placeholder{
  color:#94a3b8 !important;
}

.neo-input-modal .ant-select-arrow{
  color:#fbbf24 !important;
}

/* =========================
   DROPDOWN
========================= */

.ant-select-dropdown{
  background:#171717 !important;

  border:
    1px solid rgba(255,255,255,.08);

  border-radius:16px;

  overflow:hidden;
}

.ant-select-item{
  color:white !important;
}

.ant-select-item-option-active{
  background:
    rgba(251,191,36,.10) !important;
}

.ant-select-item-option-selected{
  background:
    rgba(251,191,36,.18) !important;

  color:#fbbf24 !important;
}

/* =========================
   DIVIDER
========================= */

.neo-modal .ant-divider{
  border-color:
    rgba(255,255,255,.08) !important;
}
/* INPUT NUMBER */

.neo-input-number{
  width:100% !important;

  height:48px !important;

  background:rgba(255,255,255,.08) !important;

  border:1px solid rgba(255,255,255,.08) !important;

  border-radius:14px !important;

  box-shadow:none !important;
}

.neo-input-number:hover,
.neo-input-number:focus-within{
  border-color:#f8cc46 !important;
}

.neo-input-number input{
  height:46px !important;

  color:white !important;

  background:transparent !important;

  font-size:15px;

  padding:0 14px !important;
}

.neo-input-number input::placeholder{
  color:rgba(255,255,255,.45);
}

/* remove spinner */

.neo-input-number .ant-input-number-handler-wrap{
  display:none;
}

/* BUTTON */

.neo-btn-create{
    height:42px !important;

    padding:0 28px !important;

    border:none !important;

    border-radius:18px !important;

    background:
        linear-gradient(
            135deg,
            #ffb224,
            #ff6600
        ) !important;

    color:white !important;

    font-weight:900;

    font-size:15px;

    box-shadow:
        0 12px 35px rgba(255,136,0,.45);

    transition:.25s;
}

.neo-btn-create:hover{
    transform:translateY(-3px);

    filter:brightness(1.05);

    box-shadow:
        0 18px 45px rgba(255,136,0,.55);
}

/* ERROR */

.ant-form-item-explain-error{
  color:#ff7b7b !important;
}

.neo-modal{
  top:32px;
  padding-bottom:32px;
}

.neo-stable-select{
  position:relative;
  width:100%;
  z-index:1;
}

.neo-stable-select.open{
  z-index:20;
}

.neo-stable-select-trigger{
  width:100%;
  height:52px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:0 14px;
  border:1px solid rgba(255,255,255,.08);
  border-radius:16px;
  background:rgba(255,255,255,.06);
  color:white;
  font:inherit;
  text-align:left;
  cursor:pointer;
  touch-action:manipulation;
  -webkit-tap-highlight-color:transparent;
}

.neo-stable-select.open .neo-stable-select-trigger{
  border-color:#fbbf24;
  box-shadow:0 0 0 3px rgba(251,191,36,.12);
}

.neo-stable-select-trigger .placeholder{
  color:#94a3b8;
}

.neo-stable-select-arrow{
  flex:none;
  color:#fbbf24;
  font-size:20px;
  line-height:1;
  transform:translateY(-2px);
}

.neo-stable-select-menu{
  position:relative;
  margin-top:4px;
  max-height:180px;
  overflow-y:auto;
  overscroll-behavior:contain;
  border:1px solid rgba(255,255,255,.08);
  border-radius:14px;
  background:#171717;
  box-shadow:0 18px 40px rgba(0,0,0,.45);
}

.neo-stable-select-option{
  width:100%;
  min-height:42px;
  display:flex;
  align-items:center;
  padding:9px 14px;
  border:0;
  background:transparent;
  color:white;
  font:inherit;
  text-align:left;
  cursor:pointer;
  touch-action:manipulation;
}

.neo-stable-select-option:hover,
.neo-stable-select-option.selected{
  background:rgba(251,191,36,.14);
}

.neo-stable-select-option.selected{
  color:#fbbf24;
}

.neo-stable-select-option:disabled{
  opacity:.42;
  cursor:not-allowed;
}

.neo-stock-empty{
  margin:-4px 0 16px;
  padding:10px 12px;
  border-radius:12px;
  background:rgba(220,38,38,.16);
  border:1px solid rgba(248,113,113,.55);
  color:#fca5a5;
  font-weight:700;
}

.neo-create-order-app-grid{
  position:relative;
  z-index:2;
  isolation:isolate;
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  column-gap:8px;
  row-gap:10px;
  width:100%;
  margin-bottom:14px;
  align-items:start;
}

.neo-create-order-app-col{
  display:flex;
  min-width:0;
  text-align:center;
}

.neo-create-order-app-button{
  position:relative;
  z-index:1;
  width:100%;
  height:96px;
  min-width:0;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
  gap:7px;
  padding:9px 3px 7px;
  box-sizing:border-box;
  border:1px solid rgba(255,255,255,.08);
  border-radius:16px;
  background:rgba(255,255,255,.045);
  color:white;
  cursor:pointer;
  /* เลือกได้ทันที แต่ยังยอมให้เลื่อน modal ในแนวตั้งบน Safari/PWA */
  touch-action:manipulation;
  user-select:none;
  -webkit-user-select:none;
  -webkit-touch-callout:none;
  -webkit-tap-highlight-color:transparent;
  transition:border-color .08s ease, background .08s ease, box-shadow .08s ease;
}

.neo-create-order-app-button:disabled{
  cursor:wait;
}

.neo-create-order-app-button:disabled:not(.active){
  opacity:.5;
}

.neo-create-order-app-loading{
  position:absolute;
  top:7px;
  right:7px;
  display:inline-flex;
  width:24px;
  height:24px;
  align-items:center;
  justify-content:center;
  border-radius:999px;
  background:rgba(0,0,0,.58);
  pointer-events:none;
}

.neo-create-order-app-loading .ant-spin-dot-item{
  background-color:#fff !important;
}

.neo-create-order-app-button:active{
  transform:none;
  border-color:#fbbf24;
  background:rgba(251,191,36,.16);
  box-shadow:0 0 0 2px rgba(251,191,36,.12);
}

.neo-create-order-app-button.active{
  border-color:#fbbf24;
  background:rgba(251,191,36,.12);
  box-shadow:0 0 0 2px rgba(251,191,36,.1);
}

.neo-create-order-app-logo,
.neo-create-order-app-logo .ant-image-img{
  width:46px !important;
  height:46px !important;
  min-width:46px !important;
  min-height:46px !important;
  border-radius:10px !important;
  object-fit:cover;
  pointer-events:none;
}

.neo-create-order-app-button .ant-image{
  display:block;
  flex:0 0 46px;
  line-height:0;
}

.neo-create-order-app-button .ant-image,
.neo-create-order-app-button .neo-create-order-app-name{
  pointer-events:none;
}

.neo-create-order-app-name{
  width:100%;
  min-height:32px;
  overflow:hidden;
  color:white !important;
  font-size:11px;
  font-weight:700;
  line-height:1.3;
  text-align:center;
  overflow-wrap:anywhere;
  display:-webkit-box;
  -webkit-box-orient:vertical;
  -webkit-line-clamp:2;
}

.neo-create-order-app-button.active .neo-create-order-app-name{
  color:#fbbf24 !important;
}

.neo-create-order-dynamic-fields{
  position:relative;
  z-index:1;
  width:100%;
  min-height:120px;
  overflow:visible;
}

/* MOBILE */

@media (max-width: 768px), (min-width: 769px) and (max-width: 1366px) and (pointer: coarse){

  .neo-modal{
    top:max(12px, env(safe-area-inset-top));
    padding:14px;
    padding-bottom:14px;
  }

  .neo-modal .ant-modal-content{
    padding:22px !important;
  }

  .neo-modal-title{
    font-size:28px;
  }

  .neo-primary-btn{
    height:46px;
  }
}

.neo-primary-btn{
  height:52px !important;

  border:none !important;

  border-radius:16px !important;

  background:
    linear-gradient(
      135deg,
      #fbbf24,
      #dc2626
    ) !important;

  color:white !important;

  font-weight:800 !important;

  box-shadow:
    0 14px 35px rgba(220,38,38,.35);

  transition:.25s;
}

.neo-primary-btn:hover{
  transform:translateY(-2px);

  filter:brightness(1.05);

  box-shadow:
    0 18px 45px rgba(220,38,38,.45);
}
      `}</style>
    </>
  );
}
